import { AxiosError, AxiosResponse } from 'axios';
import { apiClient } from './config';
import { ApiError } from '../../types';
import { getSensitiveData } from '../../utils/secureStorage';

/**
 * GET request
 * @param url - API endpoint
 * @param params - Query parameters (optional)
 */
export const get = async <T>(url: string, params?: Record<string, any>): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.get(url, { params });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * GET request with Authorization header
 * @param url - API endpoint
 * @param params - Query parameters (optional)
 */
export const getWithAuth = async <T>(url: string, params?: Record<string, any>): Promise<T> => {
  try {
    const token = await getSensitiveData("authToken"); // token stored after login

    const response: AxiosResponse<T> = await apiClient.get(url, {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      }
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
export const postWithAuthLocal = async <T>(
  url: string,
  data?: any,
  timeout?: number
): Promise<T> => {
  try {
    const token = await getSensitiveData("authToken");

    if (!token) {
      throw new Error("Authorization token missing");
    }

    const headers: any = {
      Authorization: `Bearer ${token}`,
    };

    if (!(data instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const response: AxiosResponse<T> = await apiClient.post(url, data, {
      headers,
      ...(timeout ? { timeout } : {}),
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
export const getAuth = async <T>(
  url: string,
  authToken: string,
  params?: Record<string, any>
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.get(url, {
      params,
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const postAuth = async <T>(
  url: string,
  data?: Record<string, any>
): Promise<T> => {
  try {
    const token = await getSensitiveData("authToken");

    const response: AxiosResponse<T> = await apiClient.post(url, data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
/**
 * POST request
 * @param url - API endpoint
 * @param data - Request body
 */
export const post = async <T, D = any>(
  url: string,
  data?: D,
  isFormData: boolean = false,
  token?: string,
  timeout?: number
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.post(url, data, {
      headers: {
        ...(isFormData
          ? { "Content-Type": "multipart/form-data" }
          : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(timeout ? { timeout } : {}),
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
export const postWithAuth = async <T>(
  url: string,
  data: any,
  authToken: string
): Promise<T> => {
  try {
    const headers: any = {
      Authorization: `Bearer ${authToken}`
    };

    if (data instanceof FormData) {
      delete headers["Content-Type"];
    }

    const response: AxiosResponse<T> = await apiClient.post(url, data, {
      headers
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
/**
 * PUT request
 * @param url - API endpoint
 * @param data - Request body
 */
export const put = async <T, D = any>(url: string, data?: D): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.put(url, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * PUT request with Authorization header
 * @param url - API endpoint
 * @param data - Request body
 * @param authToken - Authentication token
 */
export const putWithAuth = async <T, D = any>(
  url: string,
  data: D,
  authToken: string
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.put(url, data, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json"
      }
    });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
export const putWithAuthLocal = async <T>(
  url: string,
  data: any
): Promise<T> => {
  const token = await getSensitiveData("authToken");

  const headers: any = {
    Authorization: `Bearer ${token}`
  };

  if (!(data instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  const response = await apiClient.put(url, data, {
    headers
  });

  return response.data;
};

/**
 * PATCH request
 * @param url - API endpoint
 * @param data - Request body
 */
export const patch = async <T, D = any>(url: string, data?: D): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.patch(url, data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * DELETE request
 * @param url - API endpoint
 */
export const del = async <T>(url: string): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.delete(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
export const deleteWithAuth = async <T>(
  url: string,
  authToken: string
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await apiClient.delete(url, {
      headers: { Authorization: `Bearer ${authToken}` }
    });

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Handle API errors and format them consistently
 * @param error - Axios error object
 */
const handleApiError = (error: unknown): ApiError => {
  if (error instanceof AxiosError) {
    // Server responded with an error status
    if (error.response) {
      const { status, data } = error.response;
      
      return {
        message: data?.message || data?.error || error.message || 'An error occurred',
        code: data?.code || `HTTP_${status}`,
        details: data?.details || data,
      };
    }
    
    if (error.request) {
      return {
        message: 'No response from server. Please check your connection.',
        code: 'NETWORK_ERROR',
      };
    }
  }
  
  return {
    message: error instanceof Error ? error.message : 'An unknown error occurred',
    code: 'UNKNOWN_ERROR',
  };
};

const apiClientMethods = {
  get,
  post,
  put,
  patch,
  delete: del,
};

export default apiClientMethods;
